export const SF009_SIG_W = 220;
export const SF009_SIG_H = 64;
export const SF009_PDF_SIG_CELL_HEIGHT = 32;
export const SF009_PDF_THEME_RGB = [139, 22, 52];

export function getPdfImageType(imageSrc, fallbackType = "PNG") {
  if (!imageSrc || typeof imageSrc !== "string") return fallbackType;
  let b64 = imageSrc;
  const idx = imageSrc.indexOf("base64,");
  if (idx >= 0) b64 = imageSrc.slice(idx + 7);
  b64 = b64.replace(/\s/g, "");
  if (b64.startsWith("/9j")) return "JPEG";
  if (b64.startsWith("iVBOR")) return "PNG";
  if (b64.startsWith("UklGR")) return "WEBP";
  const m = imageSrc.match(/^data:image\/([a-zA-Z0-9+.-]+);base64,/i);
  if (m?.[1]) {
    const subtype = m[1].toLowerCase();
    if (subtype === "png") return "PNG";
    if (subtype === "jpg" || subtype === "jpeg") return "JPEG";
    if (subtype === "webp") return "WEBP";
  }
  return fallbackType;
}

export function drawSignatureImageInPdfCell(doc, data, imgSrc, columnIndex = 1) {
  if (!imgSrc || data.section !== "body" || data.column.index !== columnIndex) return;
  const pad = 2;
  const maxW = data.cell.width - pad * 2;
  const maxH = data.cell.height - pad * 2;
  if (maxW <= 0 || maxH <= 0) return;
  try {
    const format = getPdfImageType(imgSrc, "PNG");
    const props = doc.getImageProperties(imgSrc);
    const iw = props.width || SF009_SIG_W;
    const ih = props.height || SF009_SIG_H;
    const scale = Math.min(maxW / iw, maxH / ih);
    const drawW = iw * scale;
    const drawH = ih * scale;
    const x = data.cell.x + pad + (maxW - drawW) / 2;
    const y = data.cell.y + pad + (maxH - drawH) / 2;
    doc.addImage(imgSrc, format, x, y, drawW, drawH, undefined, "NONE");
  } catch {
    /* ignore bad image data */
  }
}

export function drawPdfCrossIcon(doc, x, y, size, rgb = SF009_PDF_THEME_RGB) {
  const pad = size * 0.2;
  doc.setDrawColor(...rgb);
  doc.setLineWidth(0.55);
  doc.line(x + pad, y + pad, x + size - pad, y + size - pad);
  doc.line(x + size - pad, y + pad, x + pad, y + size - pad);
}

export function toSigDataUrl(val) {
  if (!val) return "";
  const s = String(val).trim();
  if (!s) return "";
  return s.startsWith("data:image") ? s : `data:image/png;base64,${s}`;
}

export function isSigImage(val) {
  const s = String(val || "").trim();
  if (!s) return false;
  if (s.startsWith("data:image")) return true;
  return s.length > 60 && /^[A-Za-z0-9+/=]+$/.test(s.replace(/\s/g, "").slice(0, 48));
}

export function signedLabelForList(sig) {
  const s = sig?.signed;
  if (!s || isSigImage(s)) return "";
  return String(s).trim();
}

export function hydrateSigPad(ref, signedValue) {
  if (!ref?.current) return;
  ref.current.clear();
  const url = toSigDataUrl(signedValue);
  if (!url) return;
  try {
    ref.current.fromDataURL(url, { width: SF009_SIG_W, height: SF009_SIG_H });
  } catch {
    ref.current.clear();
  }
}

export function saveSigToForm(ref, sectionKey, updateFormData) {
  if (ref?.current && !ref.current.isEmpty()) {
    updateFormData(sectionKey, "signed", ref.current.getCanvas().toDataURL("image/png"));
  }
}

export function flushAllSigPads(sigRefMap, updateFormData) {
  Object.entries(sigRefMap).forEach(([sectionKey, ref]) => {
    saveSigToForm(ref, sectionKey, updateFormData);
  });
}

function formatSf009Time12(h24, min) {
  const period = h24 < 12 ? "AM" : "PM";
  let h12 = h24 % 12;
  if (h12 === 0) h12 = 12;
  return `${String(h12).padStart(2, "0")}:${String(min).padStart(2, "0")} ${period}`;
}

export const SF009_TIME_SLOT_OPTIONS = (() => {
  const out = [];
  for (let h = 0; h < 24; h++) {
    for (const m of [0, 15, 30, 45]) {
      out.push(formatSf009Time12(h, m));
    }
  }
  return out;
})();

const SF009_TIME_SLOT_SET = new Set(
  SF009_TIME_SLOT_OPTIONS.map((t) => t.toUpperCase())
);

export function normalizeSf009Time(val) {
  let s = String(val || "").trim();
  if (!s) return "";

  s = s.replace(/\s+/g, " ");
  s = s.replace(/(\s*(?:AM|PM)){2,}\s*$/i, (m) => {
    const hit = m.match(/(AM|PM)/gi);
    return hit?.length ? ` ${hit[hit.length - 1].toUpperCase()}` : "";
  });

  const ampmCompact = s.match(/^(\d{1,2}):(\d{2})(?::\d{2})?(AM|PM)$/i);
  if (ampmCompact) {
    s = `${ampmCompact[1]}:${ampmCompact[2]} ${ampmCompact[3].toUpperCase()}`;
  }

  const ampm = s.match(/^(\d{1,2}):(\d{2})(?::\d{2})?\s*(AM|PM)$/i);
  if (ampm) {
    let h = parseInt(ampm[1], 10);
    const min = Math.min(59, Math.max(0, parseInt(ampm[2], 10)));
    const period = ampm[3].toUpperCase();
    if (h < 1 || h > 12) return s;
    if (period === "AM" && h === 12) h = 0;
    if (period === "PM" && h !== 12) h += 12;
    return formatSf009Time12(h, min);
  }

  const h24 = s.match(/^(\d{1,2}):(\d{2})(?::\d{2})?$/);
  if (h24) {
    const h = Math.min(23, Math.max(0, parseInt(h24[1], 10)));
    const min = Math.min(59, Math.max(0, parseInt(h24[2], 10)));
    return formatSf009Time12(h, min);
  }

  return s;
}

export function sf009TimeSlotIncludes(val) {
  return SF009_TIME_SLOT_SET.has(normalizeSf009Time(val).toUpperCase());
}
